import { useEffect, useRef, useState } from 'react'

import { Link, Outlet, useLocation } from 'react-router-dom'

import { Footer } from './Footer'
import { Navbar } from './Navbar'

const WA_PHONE = '919418040013'
const WA_MESSAGE = encodeURIComponent('Hi, I would like to enquire about a stay at Scenic Solitude.')

function WhatsAppFAB({ sentinelRef }: { sentinelRef: React.RefObject<HTMLDivElement> }) {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setHidden(entry.isIntersecting),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [sentinelRef])

  return (
    <a
      href={`https://wa.me/${WA_PHONE}?text=${WA_MESSAGE}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full bg-[#25D366] px-4 py-3 shadow-lg hover:bg-[#20bc5a] transition-all duration-300 ${hidden ? 'opacity-0 pointer-events-none translate-y-4' : 'opacity-100 translate-y-0'}`}
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white shrink-0" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="text-[11px] font-semibold tracking-wide text-white uppercase">Chat with us</span>
    </a>
  )
}

function ReviewBanner() {
  const { pathname } = useLocation()
  if (pathname === '/leave-review') return null
  return (
    <div style={{ backgroundColor: '#1e3525' }} className="relative overflow-hidden">
      {/* subtle forest texture */}
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(circle at 10% 50%, #a3c47a 0%, transparent 50%), radial-gradient(circle at 90% 50%, #4a7c59 0%, transparent 50%)' }} />
      <div className="relative mx-auto max-w-7xl px-8 lg:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* star cluster icon */}
          <div className="flex gap-0.5 shrink-0">
            {[1,2,3,4,5].map(s => (
              <svg key={s} viewBox="0 0 16 16" className="h-4 w-4 fill-[#a3c47a]">
                <path d="M8 1l1.85 3.75L14 5.5l-3 2.92.71 4.13L8 10.5l-3.71 1.95L5 8.42 2 5.5l4.15-.75z"/>
              </svg>
            ))}
          </div>
          <div>
            <p className="text-white font-semibold text-sm leading-tight">Stayed with us?</p>
            <p className="text-white/55 text-xs">Your experience helps future guests find their mountain home.</p>
          </div>
        </div>
        <Link
          to="/leave-review"
          className="shrink-0 inline-flex items-center gap-2.5 border border-white/30 px-6 py-2.5 text-[11px] tracking-widest2 uppercase text-white hover:bg-white hover:text-ink transition-all duration-300 group"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current shrink-0">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/>
          </svg>
          Leave a Review
        </Link>
      </div>
    </div>
  )
}

export function PageWrapper() {
  const sentinelRef = useRef<HTMLDivElement>(null)

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1" style={{ paddingTop: 'var(--nav-h)' }}>
        <Outlet />
      </main>
      <ReviewBanner />
      <div ref={sentinelRef} />
      <Footer />
      <WhatsAppFAB sentinelRef={sentinelRef} />
    </div>
  )
}
